import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import StoryCard from "@/components/feature/StoryCard";
import { useSeo } from "@/hooks/useSeo";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { topics } from "@/mocks/topics";
import { stories } from "@/mocks/stories";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "专题 - 黑暗爆料",
    url: "https://heianbaoliao.example.com/topics",
    description: "把多篇爆料按主题聚合的系列策划，呈现同一条逻辑下的线索拼图。",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "爆料专题列表",
    numberOfItems: topics.length,
    itemListElement: topics.map((topic, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: topic.title,
      url: `https://heianbaoliao.example.com/topics#${topic.id}`,
    })),
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://heianbaoliao.example.com/" },
      { "@type": "ListItem", position: 2, name: "专题", item: "https://heianbaoliao.example.com/topics" },
    ],
  },
];

export default function TopicsPage() {
  useSeo({
    title: "爆料专题 - 系列策划与线索拼图｜黑暗爆料",
    description:
      "黑暗爆料专题把多篇匿名爆料按主题聚合，围绕餐饮后厨、直播电商、数据黑市与隐形条款等方向，呈现同一条逻辑下被反复提起的线索拼图。",
    keywords: "黑暗爆料专题,匿名爆料系列,行业黑幕专题,线索拼图,深度策划",
    path: "/topics",
    jsonLd,
  });

  useScrollReveal([]);

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-content px-4 py-8 md:px-6 md:py-10">
          <Breadcrumbs items={[{ name: "专题" }]} />
          <h1 className="mt-5 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
            爆料专题
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            当多篇稿件指向同一条逻辑，我们把它收进专题。专题不是新的爆料，而是把已有的线索按时间与主题重新排列，方便你看见整张图。
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-content px-4 py-10 md:px-6 md:py-12">
        <div className="space-y-14">
          {topics.map((topic) => {
            const topicStories = topic.storyIds
              .map((sid) => stories.find((s) => s.id === sid))
              .filter((s): s is (typeof stories)[number] => Boolean(s));

            return (
              <section key={topic.id} id={topic.id} className="scroll-mt-36">
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-stretch">
                  <div className="reveal lg:col-span-5">
                    <div className="relative h-64 overflow-hidden rounded-lg border border-background-200 bg-background-200 lg:h-full lg:min-h-[280px]">
                      <img
                        src={topic.cover}
                        alt={`${topic.title} 专题配图`}
                        title={`${topic.title} - 专题`}
                        className="h-full w-full object-cover object-top"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-transparent to-transparent"></div>
                      <div className="absolute inset-x-0 bottom-0 p-5">
                        <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-label font-medium text-primary-950">
                          <i className="ri-stack-line" aria-hidden="true"></i>
                          {topic.count} 篇稿件
                        </span>
                        <h2 className="mt-3 font-heading text-2xl font-bold text-background-50">{topic.title}</h2>
                      </div>
                    </div>
                  </div>

                  <div className="reveal flex flex-col lg:col-span-7">
                    <p className="text-xs font-label tracking-[0.15em] text-primary-600">{topic.subtitle}</p>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{topic.desc}</p>
                    <p className="mt-3 flex items-center gap-2 text-xs font-label text-foreground-500">
                      <i className="ri-refresh-line" aria-hidden="true"></i>
                      最近更新于 {topic.updated}
                    </p>

                    <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                      {topicStories.slice(0, 2).map((story) => (
                        <StoryCard key={story.id} story={story} />
                      ))}
                    </div>

                    {topicStories.length > 2 && (
                      <div className="mt-6">
                        {topicStories.slice(2).map((story, i) => (
                          <StoryCard key={story.id} story={story} variant="compact" index={i} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </SiteLayout>
  );
}